import React, { useEffect, useCallback } from 'react';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { ConfigProvider, theme, Spin } from 'antd';

import { AppProvider, useApp } from './context/AppContext';
import { useUserRole } from './hooks/useUserRole';
import { View, Role } from './constants/roles';
import { loginRequest } from './authConfig';

import AppLayout from './components/AppLayout';
import ImpersonationBanner from './components/ImpersonationBanner';
import ImpersonationDialog from './components/ImpersonationDialog';
import LandingPage from './pages/LandingPage';
import DashboardView from './pages/DashboardPage';
import ProjectDashboard from './pages/ProjectDashboard';
import SharedDashboardPage from './pages/SharedDashboardPage';
import ProjectWorkspace from './pages/ProjectWorkspace';
import ProjectMembers from './pages/ProjectTeamPage';
import TaskCenter from './pages/TaskCenter';
import QuestionBank from './pages/QuestionBank';
import QuestionBankCuratorPage from './pages/QuestionBankCuratorPage';
import PolicyLibrary from './pages/PolicyLibrary';
import EvidenceView from './pages/EvidenceView';
import DocumentLibraryView from './pages/DocumentLibraryView';
import ControlTaxonomy from './pages/ControlTaxonomy';
import VulnerabilityMaster from './pages/VulnerabilityMaster';
import RegulatoryDatabasePage from './pages/RegulatoryDatabasePage';
import RegulatoryLibraryPage from './pages/RegulatoryLibraryPage';
import ProjectRegulationsPage from './pages/ProjectRegulationsPage';
import NotInvitedPage from './pages/NotInvitedPage';
import LeadAdvisorsPage from './pages/LeadAdvisorsPage';
import AdministratorsPage from './pages/AdministratorsPage';
import MeetingsPage from './pages/MeetingsPage';
import AdvisoryFirmSettings from './pages/AdvisoryFirmSettings';
import SupportPage from './pages/SupportPage';
import SqaResultsPage from './pages/SqaResultsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import TrainingPage from './pages/TrainingPage';
import LibraryTemplatesPage from './pages/LibraryTemplatesPage';

import './styles/index.css';
import './styles/layout.css';

/** PolicyLens brand theme tokens for Ant Design */
const policyLensTheme = {
    algorithm: theme.defaultAlgorithm,
    token: {
        colorPrimary: '#052873',
        colorBgContainer: '#ffffff',
        colorBgLayout: '#f5f7fb',
        colorText: '#17224a',
        colorTextSecondary: '#6b7591',
        colorBorder: '#d6dcec',
        colorBorderSecondary: '#e3e7f1',
        borderRadius: 12,
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontSize: 14,
        controlHeight: 40,
    },
    components: {
        Menu: {
            darkItemBg: 'transparent',
            darkSubMenuItemBg: 'transparent',
            darkItemSelectedBg: 'rgba(255, 255, 255, 0.12)',
            darkItemColor: 'rgba(234, 234, 234, 0.78)',
            darkItemHoverColor: '#ffffff',
            darkItemSelectedColor: '#ffffff',
            itemBorderRadius: 8,
            iconSize: 18,
        },
        Layout: {
            siderBg: '#101a3d',
            headerBg: '#ffffff',
            bodyBg: '#f5f7fb',
        },
        Table: {
            headerBg: '#f0f3fa',
            headerColor: '#6b7591',
            borderRadius: 16,
        },
        Card: {
            borderRadiusLG: 16,
        },
        Modal: {
            borderRadiusLG: 20,
        },
        Button: {
            borderRadius: 10,
            controlHeight: 40,
        },
    },
};

/**
 * Inner app that relies on AppContext + MSAL state.
 */
function AppInner() {
    // Public, unauthenticated shareable-dashboard snapshot (Dashboard Redesign + SPOC
    // Visibility, backlog Sheet-1 rows 23/33) — checked BEFORE every hook/auth branch below,
    // per .claude/design/dashboard-redesign.md "Frontend additions": SharedDashboardPage.jsx
    // "is rendered without the app's MSAL-gated shell." Safe ahead of this component's other
    // hooks because nothing else in this app mutates `window.location.pathname` while AppInner
    // is mounted (this app has no client-side router — navigation is `currentView` state, not
    // URL changes) — so this branch's truthiness cannot flip mid-lifecycle for a single mounted
    // instance, which is what Rules of Hooks actually requires.
    const sharedMatch = window.location.pathname.match(/^\/shared\/([^/]+)\/?$/);
    if (sharedMatch) {
        return <SharedDashboardPage token={sharedMatch[1]} />;
    }

    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const { role, userName, userEmail, loading } = useUserRole();
    const { user, currentView, setCurrentView, signIn, activeProject } = useApp();

    // Temporary trigger for the "Act on behalf of…" dialog (Phase 1.3 week 2 — see
    // .claude/design/impersonation.md "UI affordance" > "Start flow"). A real menu action
    // ("Act on behalf of…" in the project header) is deliberately deferred to a later pass;
    // for now a Lead Advisor with a project open can reach it via Ctrl+Shift+I.
    const [impersonationDialogOpen, setImpersonationDialogOpen] = React.useState(false);
    useEffect(() => {
        if (!user || user.role !== Role.LEAD_ADVISOR) return undefined;
        const onKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i') && activeProject) {
                e.preventDefault();
                setImpersonationDialogOpen(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [user, activeProject]);

    // Trigger MSAL login directly (skips custom sign-in page)
    const handleGetStarted = useCallback(() => {
        instance.loginRedirect({
            ...loginRequest,
            prompt: 'login',
        }).catch((err) => console.error('Login failed:', err));
    }, [instance]);

    // When MSAL authenticates, sync user info into AppContext (only if a PolicyLens app role is present)
    useEffect(() => {
        if (isAuthenticated && !user && !loading && role) {
            signIn({
                id: `msal-${userEmail}`,
                name: userName,
                email: userEmail,
                role: role,
            });
        }
    }, [isAuthenticated, user, loading, role, userName, userEmail, signIn]);

    // Signed in to Microsoft but no Enterprise App role — must run before LANDING branch
    // (otherwise LANDING + !user still matches after login and shows the marketing page)
    if (isAuthenticated && !loading && role === null) {
        return <NotInvitedPage userName={userName} userEmail={userEmail} />;
    }

    // ── Pre-auth views (marketing landing only when not signed in to MSAL) ──
    if (currentView === View.LANDING && !user && !isAuthenticated) {
        return <LandingPage onEnter={handleGetStarted} />;
    }

    // If MSAL authenticated but user isn't loaded yet → loading (token resolving or signIn pending)
    if (isAuthenticated && !user) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                gap: 16,
                background: 'var(--color-bg-base)',
            }}>
                <Spin size="large" />
                <p style={{ color: '#64748b', fontSize: 14 }}>Initializing secure session...</p>
            </div>
        );
    }

    // Not authenticated and not on landing → redirect
    if (!user) {
        return <LandingPage onEnter={handleGetStarted} />;
    }

    // ── Authenticated views ──────────────────────────────────
    return (
        <>
            <AppLayout>
                {currentView === View.DASHBOARD && <DashboardView />}
                {currentView === View.LEAD_ADVISORS && <LeadAdvisorsPage />}
                {currentView === View.ADMINISTRATORS && user.role === Role.ADMIN && <AdministratorsPage />}
                {currentView === View.ADVISORY_FIRM && <AdvisoryFirmSettings />}
                {currentView === View.INTEGRATIONS && [Role.LEAD_ADVISOR, Role.ADMIN].includes(user.role) && <IntegrationsPage />}
                {currentView === View.PROJECT_WORKSPACE && activeProject && <ProjectWorkspace />}
                {currentView === View.PROJECT_DASHBOARD && activeProject && <ProjectDashboard />}
                {currentView === View.PROJECT_MEMBERS && activeProject && <ProjectMembers />}
                {currentView === View.TASK_CENTER && <TaskCenter />}
                {currentView === View.QUESTION_BANK && <QuestionBank />}
                {currentView === View.QUESTION_BANK_CURATOR && <QuestionBankCuratorPage />}
                {currentView === View.POLICY_LIBRARY && <PolicyLibrary />}
                {currentView === View.EVIDENCE && activeProject && <EvidenceView />}
                {currentView === View.DOCUMENT_LIBRARY && activeProject && <DocumentLibraryView />}
                {currentView === View.MASTER_DATA && <ControlTaxonomy />}
                {currentView === View.VULNERABILITY_MASTER && <VulnerabilityMaster />}
                {currentView === View.MEETINGS && <MeetingsPage />}
                {currentView === View.SUPPORT && <SupportPage />}
                {currentView === View.SQA_RESULTS && activeProject && <SqaResultsPage />}
                {currentView === View.TRAINING && activeProject && <TrainingPage />}
                {currentView === View.LIBRARY_TEMPLATES && <LibraryTemplatesPage />}
                {currentView === View.REGULATORY_DATABASE && <RegulatoryDatabasePage />}
                {currentView === View.REGULATORY_LIBRARY && <RegulatoryLibraryPage />}
                {currentView === View.PROJECT_REGULATIONS && activeProject && <ProjectRegulationsPage />}
            </AppLayout>
            {/* Persona-gated: only a Lead Advisor with a project open can reach this dialog
                (mount condition doubles as the gate — ImpersonationDialog does not re-check role). */}
            {user.role === Role.LEAD_ADVISOR && activeProject && (
                <ImpersonationDialog
                    open={impersonationDialogOpen}
                    onClose={() => setImpersonationDialogOpen(false)}
                    projectId={activeProject.id}
                    projectName={activeProject.companyName || activeProject.name}
                    members={activeProject.members || []}
                />
            )}
        </>
    );
}

/**
 * Root App component — wraps with MSAL + AppContext + Ant Design providers.
 */
function App({ instance }) {
    const [isDark, setIsDark] = React.useState(false);

    React.useEffect(() => {
        // Initialize state based on localStorage or document class to prevent flash
        const initialDark = localStorage.getItem('theme') === 'dark';
        if (initialDark) {
            document.documentElement.classList.add('dark');
        }
        
        const updateTheme = () => setIsDark(document.documentElement.classList.contains('dark'));
        updateTheme();
        
        const observer = new MutationObserver(updateTheme);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        
        return () => observer.disconnect();
    }, []);

    const currentTheme = {
        ...policyLensTheme,
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        // Only apply hardcoded light token colors when NOT in dark mode
        token: isDark ? {
            colorPrimary: '#3d65db',
            colorLink: '#9db8ff',
            colorBgBase: '#15171c',
            colorBgLayout: '#15171c',
            colorBgContainer: '#1e2128',
            colorBgElevated: '#23272f',
            colorBorder: '#383e4a',
            colorBorderSecondary: '#2c313b',
            colorText: '#eceef3',
            colorTextSecondary: '#9aa1b2',
            borderRadius: 12,
            fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontSize: 14,
            controlHeight: 40,
        } : policyLensTheme.token,
        components: isDark ? {
            ...policyLensTheme.components,
            Layout: { siderBg: '#0d1018', headerBg: '#1a1d23', bodyBg: '#15171c' },
            Table: { headerBg: '#23272f', headerColor: '#9aa1b2', borderRadius: 16 },
            Menu: { ...policyLensTheme.components.Menu, darkItemSelectedBg: 'rgba(157, 184, 255, 0.14)' },
        } : policyLensTheme.components,
    };

    return (
        <MsalProvider instance={instance}>
            <ConfigProvider theme={currentTheme}>
                {/* Mounted outside the routed/gated content so it is always visible whenever
                    an impersonation session is active — see .claude/design/impersonation.md
                    "UI affordance" > "Header banner". Self-gates on sessionStorage; safe to mount
                    unconditionally regardless of which auth branch AppInner is currently on. */}
                <ImpersonationBanner />
                <AppProvider>
                    <AppInner />
                </AppProvider>
            </ConfigProvider>
        </MsalProvider>
    );
}

export default App;
